import { concat, fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import {
  PrismIcon,
  PrismIconFlag,
  styleEntry,
} from '@mylighthouse/prism-ember';
import EmberTooltip from '@mylighthouse/prism-ember/components/ember-tooltip';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { task } from 'ember-concurrency';
import perform from 'ember-concurrency/helpers/perform';
import { t } from 'ember-intl';
import { and, eq, not, or } from 'ember-truth-helpers';
import type { Hotelinfo, Ota } from 'frontend-utils/models';
import type { ODSettings } from 'frontend-utils/models/hotel';
import GeneralButtonsButtonDayLiveshop from 'lumen/components/general/buttons/button-day-liveshop';
import GeneralTooltipsTooltipRatesCalendar from 'lumen/components/general/tooltips/tooltip-rates-calendar';
import HotelRatesTooltipsShortTermRentalTooltip from 'lumen/components/hotel/rates/tooltips/short-term-rental-tooltip';
import hasFeature from 'lumen/helpers/has-feature';
import type { Period } from 'lumen/models';
import type { PeriodRate } from 'lumen/models/period';
import highlightElement from 'lumen/modifiers/highlight-element';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';
import {
  compsetDifference,
  compsetDifferenceBackground,
} from 'lumen/utils/compset-difference';
import type { RateParam } from 'lumen/utils/constants/general-constants';
import findMedianRate from 'lumen/utils/numeric/median';
import { timeout } from 'lumen/utils/waiters';

interface HotelRatesCalendarsDaysDayRatesSignature {
  Args: {
    competitorsForCurrentHotelAndOTA?: Hotelinfo[];
    dayComponentHeight: number;
    dayIndex: number;
    hasDayLiveShop?: boolean;
    los: number;
    model?: Period;
    monthMoment: Dayjs;
    otaId: string;
    otaSupported?: boolean;
    params: { los: number; otaId: string; rateParam: RateParam };
    ratesEvolutionOrBookingCurveEnabled?: boolean;
    redirectToDayDetail: (date: string) => void;
    selectedOTA?: Ota;
  };
}

export default class HotelRatesCalendarsDaysDayRates extends Component<HotelRatesCalendarsDaysDayRatesSignature> {
  @service declare access: Services['access'];
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;
  @service declare intl: Services['intl'];
  @service declare liveupdate: Services['liveupdate'];
  @service declare router: Services['router'];
  @service declare usageAnalytics: Services['usage-analytics'];

  @tracked isLiveupdateTriggered: boolean = false;
  @tracked isTooltipVisible: boolean = false;

  // The last two will be 28-34, 35-41 (only for months with 6 visible weeks, otherwise only last row will be top)
  tooltipSide = this.args.dayIndex > 27 ? ('top' as const) : ('right' as const);

  trackDayHoverTask = task({ drop: true }, async (): Promise<void> => {
    const { model } = this.args;

    await timeout('extra-long');

    // We want to track if after the timeout the user is still hovering on the date
    if (this.isTooltipVisible) {
      this.usageAnalytics.track('Daily Details Viewed', {
        eventTrigger: 'calendar-view',
        view: 'calendar',
        dateViewed: model?.day?.formattedDate,
      });
    }
  });

  get allowDayLiveupdates(): boolean {
    const { hasDayLiveShop, model, otaSupported } = this.args;

    return (
      this.isFutureDay &&
      !(
        model?.isLoading ||
        this.isPartOfInactiveMonth ||
        this.isPastDayInActiveMonth ||
        this.isUpdating
      ) &&
      Boolean(hasDayLiveShop) &&
      Boolean(otaSupported)
    );
  }

  get compRateDifferenceBackground(): string {
    const { median, ownRate } = this;
    const { hotel } = this.hotelAndMarketService;

    const compsetDifferencePercentage =
      ownRate?.value && median !== undefined
        ? compsetDifference(ownRate.value, median)
        : 0;

    return compsetDifferenceBackground(
      compsetDifferencePercentage,
      hotel?.calendarColorLowerLimit ?? null,
      hotel?.calendarColorUpperLimit ?? null,
      ownRate,
    );
  }

  get compRateDifferenceFormatted(): string {
    const { median, ownRate } = this;

    if (ownRate?.value && median !== undefined) {
      const compsetDifferencePercentage = compsetDifference(
        ownRate.value,
        median,
      );

      return this.intl.formatNumber(compsetDifferencePercentage, {
        style: 'percent',
        signDisplay: 'always',
      });
    }

    return '';
  }

  get compRatesArray(): number[] {
    const { competitorsForCurrentHotelAndOTA, model } = this.args;

    const arrCompetitorRates: number[] = [];
    const ratesObj = model?.rates;

    const competitorsAndOwnHotel: Hotelinfo[] =
      competitorsForCurrentHotelAndOTA ?? [];

    competitorsAndOwnHotel.forEach((hotel) => {
      if (!hotel.id) {
        return;
      }

      const hotelRates = ratesObj?.[hotel.id];
      if (!hotelRates?.[0]) {
        return;
      }

      if (hotelRates[0].value > 0) {
        arrCompetitorRates.push(hotelRates[0].value);
      }
    });

    return arrCompetitorRates;
  }

  get dayId(): string | undefined {
    const { model } = this.args;

    if (model?.day?.date) {
      return `smart-insight-day-${model.day.date.format('YYYY-MM-DD')}`;
    }

    return undefined;
  }

  get dayNumber(): number | null {
    const { model } = this.args;

    if (model?.day?.date) {
      return model.day.date.date();
    }

    return null;
  }

  get hasOTBShortTermRentalAlert(): boolean {
    const { model } = this.args;

    return Boolean(model?.hasOTBShortTermRentalAlert);
  }

  get hasRateShortTermRentalAlert(): boolean {
    const { model } = this.args;

    return Boolean(model?.hasRateShortTermRentalAlert);
  }

  get hasRateValue(): boolean {
    return !!this.ownRate && this.ownRate.value > 0;
  }

  get hasShortTermRentalAlert(): boolean {
    if (!this.isFutureDay) {
      return false;
    }

    return this.hasOTBShortTermRentalAlert || this.hasRateShortTermRentalAlert;
  }

  get isDayLiveupdateTriggerVisible(): boolean {
    return this.isLiveupdateTriggered && !this.isPartialLiveupdateOngoing;
  }

  get isFutureDay(): boolean {
    const { model } = this.args;

    if (model?.day?.date) {
      return model.day.date.isSameOrAfter(
        this.hotelAndMarketService.getLocalTime(),
        'day',
      );
    }

    return false;
  }

  get isPartialLiveupdateOngoing(): boolean {
    const { partialLiveupdatesForThisSelection } = this.liveupdate;

    if (!partialLiveupdatesForThisSelection) {
      return false;
    }

    return partialLiveupdatesForThisSelection.length > 0;
  }

  get isPartOfInactiveMonth(): boolean {
    const { model, monthMoment } = this.args;

    if (model?.day?.date) {
      return !model.day.date.isSame(monthMoment, 'month');
    }

    return false;
  }

  get isPastDayInActiveMonth(): boolean {
    const { model } = this.args;

    if (
      model?.day?.date &&
      model.day.date.isSame(this.hotelAndMarketService.getLocalTime(), 'month')
    ) {
      return model.day.date.isBefore(
        this.hotelAndMarketService.getLocalTime(),
        'day',
      );
    }

    return false;
  }

  get isToday(): boolean {
    const { model } = this.args;

    if (model?.day?.date) {
      return model.day.date.isSame(
        this.hotelAndMarketService.getLocalTime(),
        'day',
      );
    }

    return false;
  }

  get isUpdating(): boolean {
    const { model, monthMoment } = this.args;

    return this.liveupdate.isRatesUpdating(
      monthMoment,
      model?.day.date,
      model?.rates,
    );
  }

  get median(): number | undefined {
    return findMedianRate(this.compRatesArray);
  }

  get overviewSettings(): Partial<ODSettings> {
    return this.hotelAndMarketService.hotel?.overviewSettings ?? {};
  }

  get ownRate(): PeriodRate | undefined {
    const { model } = this.args;
    const ownHotelinfoId = this.hotelAndMarketService.hotel?.hotelinfo?.id;

    if (ownHotelinfoId && model?.rates?.[ownHotelinfoId]) {
      return model.rates[ownHotelinfoId][0];
    }

    return undefined;
  }

  get ownRateFormatted(): string | null {
    return this.hotelAndMarketService.formatRate(this.ownRate ?? null, {
      longFormat: false,
    });
  }

  @action closeTooltip(event: Event): void {
    event.preventDefault();

    const { model, ratesEvolutionOrBookingCurveEnabled, redirectToDayDetail } =
      this.args;

    this.updateIsTooltipVisible(false);

    if (
      ratesEvolutionOrBookingCurveEnabled &&
      model?.fromDate &&
      !model?.isLoading
    ) {
      this.usageAnalytics.track('Rate Evolution Viewed', {
        eventTrigger: 'calendar-view',
      });

      redirectToDayDetail(model.fromDate.format('YYYY-MM-DD'));
    }
  }

  @action redirectToShortTermRental(): void {
    const { model } = this.args;

    if (!model?.fromDate) {
      return;
    }

    const currentDay = model.fromDate.format('YYYY-MM-DD');

    this.router.transitionTo('hotel.short-term-rental', {
      queryParams: {
        fromDate: currentDay,
        toDate: currentDay,
      },
    });
  }

  @action startDayLiveupdate(): void {
    const { los, model, otaId } = this.args;

    const monthsOffsetFromToday = model?.day?.date.diff(
      this.hotelAndMarketService.getLocalTime().startOf('month'),
      'month',
    );

    this.usageAnalytics.track('Live Shop Started', {
      eventTrigger: 'calendar-tile',
      los,
      monthsOffsetFromToday,
      ota: otaId,
    });

    if (this.liveupdate.isDayLiveupdateLimitReached) {
      return;
    }

    this.liveupdate.startLiveRateShop(
      false,
      null,
      null,
      dayjs(model?.fromDate).format('YYYY-MM-DD'),
    );
  }

  @action updateIsLiveupdateTriggered(value: boolean): void {
    this.isLiveupdateTriggered = value;
  }

  @action updateIsTooltipVisible(value: boolean): void {
    this.isTooltipVisible = value;
  }

  <template>
    {{! template-lint-disable no-nested-interactive require-presentational-children }}
    <span
      class="ins-calendar-day-container
        {{if @ratesEvolutionOrBookingCurveEnabled 'detail-enabled'}}"
      role="tooltip"
      {{on "mouseenter" (fn this.updateIsLiveupdateTriggered true)}}
      {{on "mouseenter" (perform this.trackDayHoverTask)}}
      {{on "mouseleave" (fn this.updateIsLiveupdateTriggered false)}}
    >
      <div
        class="ins-calendar-day"
        style={{styleEntry "height" (concat @dayComponentHeight "px")}}
        {{highlightElement id=this.dayId}}
      >
        <div
          class="calendar-day-content
            {{if
              (or this.isPartOfInactiveMonth this.isPastDayInActiveMonth)
              'inactive'
            }}"
        >
          <div class="clearfix">
            <div class="left-column flex items-center justify-between gap-100">
              <div
                class="ins-day-number text-200--medium
                  {{if this.isToday 'today'}}"
              >
                {{this.dayNumber}}
              </div>
              <GeneralButtonsButtonDayLiveshop
                @isDayLiveupdateLimitReached={{this.liveupdate.isDayLiveupdateLimitReached}}
                @isUpdating={{this.isUpdating}}
                @isVisible={{and
                  this.allowDayLiveupdates
                  this.isDayLiveupdateTriggerVisible
                }}
                @startDayLiveupdate={{this.startDayLiveupdate}}
              />
            </div>

            <div class="right-column flex items-center gap-100">
              {{#if (hasFeature "app.events")}}
                {{#if @model.day.calendarEvents.length}}
                  <PrismIcon @name="calendar-accepted" @size="extra-small" />
                {{/if}}
                {{#each @model.day.holidayCountries as |country|}}
                  {{#if (eq country "multiple")}}
                    <PrismIcon @name="events-global" @size="extra-small" />
                  {{else}}
                    <PrismIconFlag @code={{country}} @size="small" />
                  {{/if}}
                {{/each}}
              {{/if}}

              {{#if
                (and
                  this.access.hasShortTermRentalAccess
                  (not this.access.hasShortTermRentalOnlyStrTab)
                )
              }}
                <button
                  class="button--unstyled button-short-term-rental"
                  data-event-id="hotel_rates-calendar_day_house_icon-click"
                  type="button"
                  {{on "click" this.redirectToShortTermRental}}
                >
                  <PrismIcon @name="short-term-rental" @size="extra-small" />
                  {{#if this.hasShortTermRentalAlert}}
                    <span class="alert"></span>
                  {{/if}}

                  <EmberTooltip
                    @side="right"
                    @tooltipClass="base-tooltip tooltip-short-term-rental"
                  >
                    <HotelRatesTooltipsShortTermRentalTooltip
                      @hasOTBShortTermRentalAlert={{this.hasOTBShortTermRentalAlert}}
                      @hasRateShortTermRentalAlert={{this.hasRateShortTermRentalAlert}}
                      @hasShortTermRentalAlert={{this.hasShortTermRentalAlert}}
                      {{! @glint-expect-error: Incorrect type }}
                      @period={{@model}}
                      @showChanges={{true}}
                    />
                  </EmberTooltip>
                </button>
              {{/if}}
            </div>
          </div>

          <div class="day-info-block-wrapper">
            {{#unless @model.isLoading}}
              <div
                class="day-info-aligned-block
                  {{this.compRateDifferenceBackground}}"
              >
                <div
                  role="button"
                  {{on "click" this.closeTooltip}}
                  {{on "mouseenter" (fn this.updateIsTooltipVisible true)}}
                  {{on "mouseleave" (fn this.updateIsTooltipVisible false)}}
                >
                  <div
                    class="own-rate-container
                      {{unless this.hasRateValue 'own-rate-container--empty'}}"
                  >
                    <p>{{this.ownRateFormatted}}</p>
                  </div>
                  {{#if this.hasRateValue}}
                    <div class="comp-percentage-container">
                      <p class="text-200--medium">
                        {{this.compRateDifferenceFormatted}}
                        {{t "rates.vscomp"}}
                      </p>
                    </div>
                  {{/if}}
                </div>
              </div>
            {{/unless}}
          </div>
        </div>

        {{#unless @model.isLoading}}
          {{#if this.isTooltipVisible}}
            <EmberTooltip
              @isShown={{this.isTooltipVisible}}
              @side={{this.tooltipSide}}
              @tooltipClass="rates-calendar-tooltip"
            >
              <GeneralTooltipsTooltipRatesCalendar
                @competitorsForCurrentHotelAndOTA={{@competitorsForCurrentHotelAndOTA}}
                @fetchOtherLosRates={{true}}
                @isHovered={{this.isTooltipVisible}}
                @median={{this.median}}
                @otaId={{@selectedOTA.id}}
                @params={{@params}}
                @period={{@model}}
              />
            </EmberTooltip>
          {{/if}}
        {{/unless}}
      </div>
    </span>
  </template>
}
